// 图床缩略图 (2026-09-16).
//
// 起因: 资产各页的列表/图墙一直**直出原图**, 页面滑不动甚至卡死。实测 (美服生产):
//   - 效果图资产 / UGC 封面 (storage-us): 原图 **4096×4096, 15.8 MB** —— 塞进 48×48 或 130px 的格子,
//     一页二十来行就是几百 MB 位图, 这才是"其它页面也划不动"的根源;
//   - c10s 订单图墙 (storage-smb / 阿里云 OSS): 一页 35 张合计 7.9 MB (单张最大 1.1 MB)。
// 下载量不是主因, **解码与位图内存**才是, 所以表现为滚动掉帧 / 整页卡死。
//
// 两类图床各有自己的处理机制 (都已实测):
//   - storage-us.atomm.com: 同域 Cloudflare Images —— /cdn-cgi/image/<opts>/<path>
//     实测 15.8 MB → **2.1 KB (avif, 浏览器带 Accept 时)**;
//     (它对 ?x-oss-process 会 301 到 image-res.atomm.com, 那个域名对国服桶路径返回 401,
//      所以不做跨域名映射, 就走同域的 cdn-cgi, 稳)
//   - res-shanghai.oss-cn-shanghai.aliyuncs.com / storage-smb.atomm.com: 阿里云 OSS 图片处理
//     ?x-oss-process=image/resize,... 实测 509 KB → **4.8 KB**。
//
// ⚠ 只对**白名单域名**动手: 其它来源 (第三方、平台附件代理) 原样返回 —— 乱挂参数可能被当成
// 未知 query 或直接 404, 那就从"图有点大"变成"图没了"。原图本身 404 的, 加参数后同样 404,
// 行为一致 (已实测), 不会把好图弄坏。

/** 阿里云 OSS 图片处理 (query 参数) */
const OSS_HOSTS = [
    'res-shanghai.oss-cn-shanghai.aliyuncs.com', // 国服 c10s / smb 图床
    'storage-smb.atomm.com', // 美服 c10s / smb 图床
    // 2026-09-17 补: 商品采集中心 (material-collector) 的图走这个桶, 之前漏登记 ——
    // 页面虽然调了 thumbUrl, 但域名不在白名单就原样返回原图, 于是列表直出 165 KB/张。
    // 实测加参数后 165,848 B → 3,116 B (webp), 小 53 倍。
    'storage-public.atomm.com',
];

// ⚠ prism 的加工/识别图在 mbtempstore.blob.core.windows.net (Azure Blob) —— **不支持任何
// 缩放参数** (实测 ?x-oss-process / ?width 都原样返回 190 KB 原图), 所以加工数据中心、
// 耗材识别中心的图目前没有缩略图可用, 后端 thumbURL() 对它同样无效。
// 要治只能从图床侧走: prism 上传时另存一份缩略图, 或给这个 blob 挂能改尺寸的 CDN。

/** Cloudflare Images (同域路径前缀) */
const CF_HOSTS = [
    'storage-us.atomm.com', // 效果图资产 / UGC / 材料图
];

/** 放大查看的宽度: 1600 足够看清加工细节, 又能避开 4096×4096/15.8MB 原图 */
export const VIEW_W = 1600;

/** 列表/图墙默认宽度: 格子最宽约 200px, 给 320 让 2x 屏也不糊 */
export const THUMB_W = 320;
/** 表格里的小方图 (48px 级) 用这个 */
export const THUMB_W_SM = 96;

/**
 * thumbUrl 把图床地址转成缩略图地址 (只缩尺寸, 不裁剪, 保持比例)。
 *
 * - 非白名单域名 / 空值: 原样返回;
 * - 已经带了处理参数的: 原样返回 (调用方自己指定了, 别叠加);
 * - OSS 系用 query 拼 (已有 query 则用 &), Cloudflare 系改写路径前缀。
 *
 * 放大查看用 viewUrl(), 别直接给原图 —— 见那支函数的注释。
 */
export function thumbUrl(src: string | null | undefined, w: number = THUMB_W): string {
    if (!src) return '';
    if (src.includes('x-oss-process=') || src.includes('/cdn-cgi/image/')) return src;
    let u: URL;
    try {
        u = new URL(src, window.location.origin);
    } catch {
        return src; // 解不出来就别动它
    }
    if (OSS_HOSTS.includes(u.hostname)) {
        const sep = src.includes('?') ? '&' : '?';
        return `${src}${sep}x-oss-process=image/resize,w_${w}/format,webp/quality,q_80`;
    }
    if (CF_HOSTS.includes(u.hostname)) {
        // /cdn-cgi/image/ 要放在路径最前面, 后面接原始路径 (不含前导斜杠)
        const path = u.pathname.replace(/^\//, '') + u.search;
        return `${u.origin}/cdn-cgi/image/width=${w},format=auto,quality=80/${path}`;
    }
    return src;
}

/**
 * viewUrl 放大查看用的地址 (ImageLightbox)。
 *
 * 原先这里一律给原图, 直到 2026-09-22 在线上踩到 **HEIC 伪装成 .png**: iPhone 直传的识别图
 * 文件名与 Content-Type 都是 image/png, 实际魔数 ftypheic, Chrome 解不了 —— 列表缩略图走
 * Cloudflare Images 反而正常 (它能解 HEIC, 实测 2.3MB HEIC → 1.8KB avif), 点开看大图却因为
 * 用原图而裂掉。而阿里云那条 (image-res) 对 HEIC 完全不处理, 原样返回 2.3MB。
 *
 * 所以放大查看也走图床处理, 只是把宽度放到 VIEW_W:
 *   - Cloudflare 系: 顺带解了 HEIC, 也避开 4096×4096/15.8MB 原图直出;
 *   - OSS 系与白名单外: 原样返回原图 (它们的原图本来就能显示)。
 */
export function viewUrl(src: string | null | undefined): string {
    if (!src) return '';
    return thumbUrl(src, VIEW_W);
}
